import { api, ApiError } from "@/lib/api/client";
import type { components } from "@/types/api";
import type { StepUpProof } from "./stepup.api";

export type Account = components["schemas"]["Account"];
export type AccountResponse = components["schemas"]["AccountResponse"];
export type PasswordChangeRequest = components["schemas"]["PasswordChangeRequest"];
export type PasswordChangeResponse = components["schemas"]["PasswordChangeResponse"];
export type AccountDeletionResponse = components["schemas"]["AccountDeletionResponse"];
export type DeletionBlockReason =
  components["schemas"]["AccountDeletionBlockedErrorResponse"]["reasons"][number];

const ACCOUNT_DELETION_BLOCKED_CODE = "account_deletion_blocked";

const KNOWN_BLOCK_REASONS: Record<DeletionBlockReason, true> = {
  apps: true,
  subscription: true,
  pending_transfer: true,
};

/**
 * GET /v1/account
 */
export function getAccount(): Promise<AccountResponse> {
  return api.get<AccountResponse>("/v1/account");
}

/**
 * POST /v1/account/password
 * Signs out the other browser sessions; this one, CLI logins and API keys stay.
 */
export function changePassword(body: PasswordChangeRequest): Promise<PasswordChangeResponse> {
  return api.post<PasswordChangeResponse>("/v1/account/password", body);
}

/**
 * POST /v1/account/deletion
 * Schedules the purge 30 days out and revokes every credential of the account at once.
 */
export function requestAccountDeletion(
  password: string,
  stepUp?: StepUpProof,
): Promise<AccountDeletionResponse> {
  return api.post<AccountDeletionResponse>("/v1/account/deletion", {
    password,
    ...(stepUp ? { stepUp } : {}),
  });
}

/**
 * DELETE /v1/account/deletion
 * Lifts the freeze; credentials revoked at request time stay revoked.
 */
export function cancelAccountDeletion(): Promise<void> {
  return api.delete<void>("/v1/account/deletion");
}

export function deletionBlockReasons(err: unknown): DeletionBlockReason[] | null {
  if (
    !(err instanceof ApiError) ||
    err.status !== 409 ||
    err.code !== ACCOUNT_DELETION_BLOCKED_CODE
  ) {
    return null;
  }
  const known = (err.reasons ?? []).filter((reason): reason is DeletionBlockReason =>
    Object.hasOwn(KNOWN_BLOCK_REASONS, reason),
  );
  return known.length > 0 ? known : null;
}
